import { useState, useCallback } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Button,
  Modal,
  Drawer,
  TextInput,
  Textarea,
  Select,
  Badge,
  SegmentedControl,
  Divider,
  ActionIcon,
  Tooltip,
  Rating,
  Skeleton,
  Pagination,
  ThemeIcon,
  Alert,
  Loader,
  Collapse,
  ScrollArea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import {
  IconHeadset,
  IconPlus,
  IconEye,
  IconSend,
  IconSparkles,
  IconRobot,
  IconUser,
  IconBulb,
  IconChevronDown,
  IconChevronUp,
  IconMessageChatbot,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import {
  useSupportTickets,
  useSupportTicket,
  useCreateSupportTicket,
  useRespondToTicket,
  useRateSupportTicket,
  useAIChatSend,
} from '@/hooks/usePortalApi';
import { useAuthStore } from '@/store/auth';
import type { SupportTicketSummary } from '@/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const PAGE_SIZE = 20;

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'billing', label: 'Billing' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'product', label: 'Product' },
  { value: 'technical', label: 'Technical' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const QUICK_PROMPTS = [
  { label: 'Where is my order?', prompt: 'Where is my most recent order? What is the delivery status?' },
  { label: 'Invoice question', prompt: 'I have a question about my latest invoice. Can you show me the details?' },
  { label: 'Return an item', prompt: 'I need to return an item from a recent order. What is the process?' },
  { label: 'Delivery issue', prompt: 'I had a problem with my last delivery. Can you help me report it?' },
];

function getPriorityColor(priority: string): string {
  switch (priority.toLowerCase()) {
    case 'urgent':
      return 'red';
    case 'high':
      return 'orange';
    case 'medium':
      return 'yellow';
    case 'low':
      return 'gray';
    default:
      return 'gray';
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ChatMsg {
  role: 'user' | 'bot';
  content: string;
}

function AIAssistantPanel() {
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const chatMutation = useAIChatSend();
  const auth = useAuthStore();
  const customerId = (auth as any).user?.customer_id ?? 0;

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    const userMsg: ChatMsg = { role: 'user', content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    chatMutation.mutate(
      { message: text.trim(), customer_id: customerId },
      {
        onSuccess: (res: any) => {
          const reply = res?.data?.response ?? res?.data?.message ?? 'I can help you with that. Would you like me to create a support ticket?';
          setMessages((prev) => [...prev, { role: 'bot', content: reply }]);
        },
        onError: () => {
          setMessages((prev) => [
            ...prev,
            { role: 'bot', content: 'I\'m having trouble connecting right now. Please try again or create a support ticket below.' },
          ]);
        },
      },
    );
  };

  return (
    <Paper
      radius="md"
      withBorder
      style={{
        border: '1px solid var(--mantine-color-blue-3)',
        background: 'linear-gradient(135deg, var(--mantine-color-blue-0) 0%, var(--mantine-color-indigo-0) 100%)',
      }}
    >
      {/* Header — always visible */}
      <Group
        justify="space-between"
        p="md"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="blue" radius="xl">
            <IconMessageChatbot size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600} c="blue.8">AI Assistant</Text>
            <Text size="xs" c="dimmed">Get instant answers before creating a ticket</Text>
          </div>
        </Group>
        <ActionIcon variant="subtle" color="blue">
          {expanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
        </ActionIcon>
      </Group>

      <Collapse in={expanded}>
        <Divider color="blue.2" />
        <Stack gap="sm" p="md">
          {/* Quick prompts */}
          {messages.length === 0 && (
            <>
              <Text size="sm" c="dimmed">Try asking about:</Text>
              <Group gap="xs">
                {QUICK_PROMPTS.map((qp) => (
                  <Button
                    key={qp.label}
                    size="xs"
                    variant="light"
                    color="blue"
                    leftSection={<IconBulb size={14} />}
                    onClick={() => sendMessage(qp.prompt)}
                  >
                    {qp.label}
                  </Button>
                ))}
              </Group>
            </>
          )}

          {/* Chat messages */}
          {messages.length > 0 && (
            <ScrollArea h={250} offsetScrollbars>
              <Stack gap="sm">
                {messages.map((msg, i) => (
                  <Group
                    key={i}
                    align="flex-start"
                    gap="xs"
                    style={{
                      flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                    }}
                  >
                    <ThemeIcon
                      size="sm"
                      variant="light"
                      color={msg.role === 'user' ? 'gray' : 'blue'}
                      radius="xl"
                      mt={2}
                    >
                      {msg.role === 'user' ? <IconUser size={12} /> : <IconRobot size={12} />}
                    </ThemeIcon>
                    <Paper
                      p="xs"
                      radius="md"
                      maw="80%"
                      style={{
                        background: msg.role === 'user'
                          ? 'var(--mantine-color-gray-1)'
                          : 'var(--mantine-color-blue-1)',
                      }}
                    >
                      <Text size="sm">{msg.content}</Text>
                    </Paper>
                  </Group>
                ))}
                {chatMutation.isPending && (
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color="blue" radius="xl">
                      <IconRobot size={12} />
                    </ThemeIcon>
                    <Loader size="xs" color="blue" />
                  </Group>
                )}
              </Stack>
            </ScrollArea>
          )}

          {/* Input */}
          <Group gap="xs">
            <TextInput
              style={{ flex: 1 }}
              placeholder="Ask a question..."
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(input); }}
              leftSection={<IconSparkles size={16} />}
            />
            <ActionIcon
              size="lg"
              variant="filled"
              color="blue"
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || chatMutation.isPending}
            >
              <IconSend size={16} />
            </ActionIcon>
          </Group>

          <Text size="xs" c="dimmed" ta="center">
            Can't find what you need? Create a support ticket below for personalized help.
          </Text>
        </Stack>
      </Collapse>
    </Paper>
  );
}

export default function SupportCenter() {
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTicketId, setSelectedTicketId] = useState(0);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  // New ticket form state
  const [newCategory, setNewCategory] = useState<string | null>(null);
  const [newPriority, setNewPriority] = useState<string | null>(null);
  const [newSubject, setNewSubject] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // Reply state
  const [replyMessage, setReplyMessage] = useState('');

  // Rating state
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');

  const { data: ticketsData, isLoading } = useSupportTickets({
    status: statusFilter || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const { data: ticketDetail, isLoading: detailLoading } = useSupportTicket(selectedTicketId);

  const createTicket = useCreateSupportTicket();
  const respondToTicket = useRespondToTicket();
  const rateTicket = useRateSupportTicket();

  const tickets = ticketsData?.items ?? [];
  const totalPages = Math.ceil((ticketsData?.total ?? 0) / PAGE_SIZE);

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleViewTicket = useCallback(
    (ticket: SupportTicketSummary) => {
      setSelectedTicketId(ticket.id);
      openDrawer();
    },
    [openDrawer],
  );

  const handleCloseDrawer = () => {
    closeDrawer();
    setSelectedTicketId(0);
    setReplyMessage('');
    setRatingValue(0);
    setRatingComment('');
  };

  const resetForm = () => {
    setNewCategory(null);
    setNewPriority(null);
    setNewSubject('');
    setNewDescription('');
  };

  const handleCreateTicket = () => {
    if (!newSubject.trim() || !newCategory || !newPriority || !newDescription.trim()) {
      notifications.show({
        title: 'Missing fields',
        message: 'Please fill in all required fields.',
        color: 'red',
      });
      return;
    }
    createTicket.mutate(
      {
        subject: newSubject.trim(),
        category: newCategory,
        priority: newPriority,
        description: newDescription.trim(),
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Ticket created',
            message: 'Your support ticket has been submitted.',
            color: 'green',
          });
          closeModal();
          resetForm();
        },
        onError: () => {
          notifications.show({
            title: 'Error',
            message: 'Failed to create ticket. Please try again.',
            color: 'red',
          });
        },
      },
    );
  };

  const handleSendReply = () => {
    if (!replyMessage.trim() || selectedTicketId === 0) return;
    respondToTicket.mutate(
      { id: selectedTicketId, message: replyMessage.trim() },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Reply sent',
            message: 'Your response has been submitted.',
            color: 'green',
          });
          setReplyMessage('');
        },
        onError: () => {
          notifications.show({
            title: 'Error',
            message: 'Failed to send reply. Please try again.',
            color: 'red',
          });
        },
      },
    );
  };

  const handleSubmitRating = () => {
    if (ratingValue === 0 || selectedTicketId === 0) return;
    rateTicket.mutate(
      {
        id: selectedTicketId,
        rating: ratingValue,
        comment: ratingComment.trim() || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Rating submitted',
            message: 'Thank you for your feedback!',
            color: 'green',
          });
          setRatingValue(0);
          setRatingComment('');
        },
        onError: () => {
          notifications.show({
            title: 'Error',
            message: 'Failed to submit rating. Please try again.',
            color: 'red',
          });
        },
      },
    );
  };

  const renderSlaCountdown = (slaDue: string | null) => {
    if (!slaDue) return <Text size="sm" c="dimmed">--</Text>;
    const now = dayjs();
    const due = dayjs(slaDue);
    if (due.isBefore(now)) {
      return (
        <Text size="sm" c="red" fw={500}>
          Overdue
        </Text>
      );
    }
    return (
      <Text size="sm" c="orange" fw={500}>
        {due.fromNow(true)} left
      </Text>
    );
  };

  const renderTicketsTable = () => {
    if (tickets.length === 0) {
      return (
        <EmptyState
          icon={<IconHeadset size={48} />}
          title="No tickets found"
          description={
            statusFilter
              ? 'No tickets match the selected filter.'
              : 'You have no support tickets yet. Create one to get help.'
          }
          action={
            !statusFilter ? (
              <Button leftSection={<IconPlus size={16} />} onClick={openModal}>
                New Ticket
              </Button>
            ) : undefined
          }
        />
      );
    }

    return (
      <Paper withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ticket #</Table.Th>
              <Table.Th>Subject</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Priority</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>SLA</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th w={80} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tickets.map((ticket) => (
              <Table.Tr key={ticket.id}>
                <Table.Td>
                  <Text size="sm" fw={500} style={{ fontFamily: 'monospace' }}>
                    {ticket.ticket_number}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={1}>
                    {ticket.subject}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" size="sm">
                    {ticket.category}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge color={getPriorityColor(ticket.priority)} size="sm">
                    {ticket.priority}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <StatusBadge status={ticket.status} />
                </Table.Td>
                <Table.Td>{renderSlaCountdown(ticket.sla_resolution_due)}</Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {ticket.created_at
                      ? dayjs(ticket.created_at).format('MMM D, YYYY')
                      : '--'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Tooltip label="View ticket">
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={() => handleViewTicket(ticket)}
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    );
  };

  const renderDrawerContent = () => {
    if (detailLoading) {
      return (
        <Stack gap="md">
          <Skeleton height={30} />
          <Skeleton height={60} />
          <Skeleton height={200} />
        </Stack>
      );
    }

    if (!ticketDetail) return null;

    const visibleResponses = ticketDetail.responses.filter((r: any) => !r.is_internal);
    const isResolved =
      ticketDetail.status === 'resolved' || ticketDetail.status === 'closed';

    return (
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="sm">
            <Text size="lg" fw={700} style={{ fontFamily: 'monospace' }}>
              {ticketDetail.ticket_number}
            </Text>
            <StatusBadge status={ticketDetail.status} />
            <Badge color={getPriorityColor(ticketDetail.priority)} size="sm">
              {ticketDetail.priority}
            </Badge>
          </Group>
        </Group>

        {/* Subject */}
        <Text fw={600}>{ticketDetail.subject}</Text>

        {/* Description */}
        <Paper p="sm" withBorder bg="gray.0">
          <Text size="sm">{ticketDetail.description}</Text>
        </Paper>

        <Divider />

        {/* Response Thread */}
        <Text fw={600} size="sm">
          Responses ({visibleResponses.length})
        </Text>

        {visibleResponses.length === 0 ? (
          <Text size="sm" c="dimmed">
            No responses yet.
          </Text>
        ) : (
          <Stack gap="sm">
            {visibleResponses.map((response: any) => {
              const isCustomer = response.author_role === 'customer';
              return (
                <Paper
                  key={response.id}
                  p="sm"
                  withBorder
                  style={{
                    marginLeft: isCustomer ? 40 : 0,
                    marginRight: isCustomer ? 0 : 40,
                    borderColor: isCustomer
                      ? 'var(--mantine-color-blue-3)'
                      : 'var(--mantine-color-gray-3)',
                  }}
                >
                  <Group justify="space-between" mb={4}>
                    <Group gap="xs">
                      <Text size="sm" fw={600}>
                        {response.author}
                      </Text>
                      <Badge variant="outline" size="xs">
                        {response.author_role}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {response.created_at
                        ? dayjs(response.created_at).format('MMM D, YYYY h:mm A')
                        : ''}
                    </Text>
                  </Group>
                  <Text size="sm">{response.message}</Text>
                </Paper>
              );
            })}
          </Stack>
        )}

        {/* Attachments */}
        {ticketDetail.attachments.length > 0 && (
          <>
            <Divider />
            <Text fw={600} size="sm">
              Attachments ({ticketDetail.attachments.length})
            </Text>
            <Stack gap="xs">
              {ticketDetail.attachments.map((att: any) => (
                <Group key={att.id} gap="xs">
                  <Text size="sm" fw={500}>
                    {att.filename}
                  </Text>
                  <Text size="xs" c="dimmed">
                    ({formatFileSize(att.file_size)})
                  </Text>
                </Group>
              ))}
            </Stack>
          </>
        )}

        <Divider />

        {/* Reply Input */}
        {!isResolved && (
          <Stack gap="xs">
            <Textarea
              placeholder="Type your reply..."
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.currentTarget.value)}
              minRows={3}
              autosize
            />
            <Group justify="flex-end">
              <Button
                leftSection={<IconSend size={16} />}
                onClick={handleSendReply}
                loading={respondToTicket.isPending}
                disabled={!replyMessage.trim()}
              >
                Send Reply
              </Button>
            </Group>
          </Stack>
        )}

        {/* Rating Section (after resolution) */}
        {isResolved && (
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Text fw={600} size="sm">
                Rate this support experience
              </Text>
              {ticketDetail.satisfaction_rating != null ? (
                <Group gap="xs">
                  <Rating value={ticketDetail.satisfaction_rating} readOnly />
                  <Text size="sm" c="dimmed">
                    Thank you for your feedback!
                  </Text>
                </Group>
              ) : (
                <>
                  <Rating value={ratingValue} onChange={setRatingValue} />
                  <Textarea
                    placeholder="Optional comment..."
                    value={ratingComment}
                    onChange={(e) => setRatingComment(e.currentTarget.value)}
                    minRows={2}
                    autosize
                  />
                  <Group justify="flex-end">
                    <Button
                      size="sm"
                      onClick={handleSubmitRating}
                      loading={rateTicket.isPending}
                      disabled={ratingValue === 0}
                    >
                      Submit Rating
                    </Button>
                  </Group>
                </>
              )}
            </Stack>
          </Paper>
        )}
      </Stack>
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Support Center"
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={openModal}>
            New Ticket
          </Button>
        }
      />

      {/* AI Assistant Panel */}
      <AIAssistantPanel />

      <SegmentedControl
        value={statusFilter}
        onChange={handleStatusChange}
        data={STATUS_TABS}
      />

      {isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : (
        renderTicketsTable()
      )}

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Group>
      )}

      {/* New Ticket Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => {
          closeModal();
          resetForm();
        }}
        title={
          <Text fw={700} size="lg">
            Create Support Ticket
          </Text>
        }
        size="lg"
      >
        <Stack gap="md">
          {/* AI suggestion banner */}
          <Alert
            color="blue"
            variant="light"
            icon={<IconSparkles size={16} />}
            radius="md"
          >
            <Text size="sm">
              <Text span fw={600}>Tip:</Text> Try the AI Assistant above first — it can often
              resolve common questions instantly without waiting for a response.
            </Text>
          </Alert>

          <Select
            label="Category"
            placeholder="Select a category"
            data={CATEGORY_OPTIONS}
            value={newCategory}
            onChange={setNewCategory}
            required
          />
          <Select
            label="Priority"
            placeholder="Select priority"
            data={PRIORITY_OPTIONS}
            value={newPriority}
            onChange={setNewPriority}
            required
          />
          <TextInput
            label="Subject"
            placeholder="Brief description of your issue"
            value={newSubject}
            onChange={(e) => setNewSubject(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Description"
            placeholder="Describe your issue in detail..."
            value={newDescription}
            onChange={(e) => setNewDescription(e.currentTarget.value)}
            minRows={4}
            autosize
            required
          />
          <Group justify="flex-end">
            <Button
              variant="light"
              onClick={() => {
                closeModal();
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateTicket} loading={createTicket.isPending}>
              Submit Ticket
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Ticket Detail Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={handleCloseDrawer}
        position="right"
        size="lg"
        title={
          <Text fw={700} size="lg">
            Ticket Details
          </Text>
        }
      >
        {renderDrawerContent()}
      </Drawer>
    </Stack>
  );
}
